import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { borderRadius, layoutGap } from '../../../theme/theme';
import type { ExportLogEntry, ExportLogStatus } from '../lib/exportRunLog';

interface ExportRunLogEntriesProps {
  readonly entries: readonly ExportLogEntry[];
}

const statusMeta = {
  success: {
    color: 'success',
    Icon: CheckCircleOutlineIcon,
  },
  warning: {
    color: 'warning',
    Icon: WarningAmberIcon,
  },
  failure: {
    color: 'error',
    Icon: ErrorOutlineIcon,
  },
} as const satisfies Record<
  ExportLogStatus,
  {
    readonly color: 'success' | 'warning' | 'error';
    readonly Icon: typeof CheckCircleOutlineIcon;
  }
>;

export default function ExportRunLogEntries({ entries }: ExportRunLogEntriesProps) {
  const theme = useTheme();

  if (entries.length === 0) {
    return (
      <Typography
        role="status"
        variant="body2"
        sx={{ color: theme.palette.text.secondary, py: 2 }}
      >
        No export log entries match the current filters.
      </Typography>
    );
  }

  return (
    <Box
      role="list"
      aria-label="PCF export outcomes"
      sx={{ display: 'flex', flexDirection: 'column', gap: layoutGap.dense }}
    >
      {entries.map((entry) => {
        const meta = statusMeta[entry.status];
        const statusColor = theme.palette[meta.color].main;
        const StatusIcon = meta.Icon;
        return (
          <Box
            role="listitem"
            key={entry.id}
            sx={{
              display: 'grid',
              gridTemplateColumns: 'auto minmax(0, 1fr)',
              gap: layoutGap.row,
              alignItems: 'start',
              p: 1.25,
              border: `1px solid ${alpha(statusColor, 0.35)}`,
              borderRadius: `${borderRadius.md}px`,
              backgroundColor: alpha(statusColor, 0.05),
            }}
          >
            <StatusIcon
              aria-hidden="true"
              sx={{ color: statusColor, fontSize: 20, mt: 0.125 }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}
              >
                {entry.subject}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text.secondary, overflowWrap: 'anywhere' }}
              >
                {entry.message}
              </Typography>
              {(entry.stage || entry.exceptionType || entry.elementCount !== undefined) && (
                <Box
                  component="dl"
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'max-content minmax(0, 1fr)',
                    columnGap: layoutGap.row,
                    rowGap: 0.25,
                    mt: 0.75,
                    mb: 0,
                    '& dt': { color: theme.palette.text.secondary, fontWeight: 700 },
                    '& dd': { m: 0, overflowWrap: 'anywhere' },
                  }}
                >
                  {entry.stage && (
                    <>
                      <Typography component="dt" variant="caption">Stage</Typography>
                      <Typography component="dd" variant="caption">{entry.stage}</Typography>
                    </>
                  )}
                  {entry.exceptionType && (
                    <>
                      <Typography component="dt" variant="caption">Cause type</Typography>
                      <Typography component="dd" variant="caption">{entry.exceptionType}</Typography>
                    </>
                  )}
                  {entry.elementCount !== undefined && (
                    <>
                      <Typography component="dt" variant="caption">Affected elements</Typography>
                      <Typography component="dd" variant="caption">{entry.elementCount}</Typography>
                    </>
                  )}
                  {(entry.elementIds?.length ?? 0) > 0 && (
                    <>
                      <Typography component="dt" variant="caption">Revit element IDs</Typography>
                      <Typography component="dd" variant="caption">
                        {entry.elementIds!.join(', ')}
                        {entry.additionalElementCount
                          ? ` (+${entry.additionalElementCount} more)`
                          : ''}
                      </Typography>
                    </>
                  )}
                </Box>
              )}
              {entry.suggestedAction && (
                <Box
                  sx={{
                    mt: 1,
                    p: 1,
                    borderRadius: `${borderRadius.sm}px`,
                    backgroundColor: alpha(statusColor, 0.08),
                  }}
                >
                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 700 }}>
                    What to do
                  </Typography>
                  <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                    {entry.suggestedAction}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
